import React, { useState, useEffect } from 'react';
import { 
  Cloud, 
  CloudUpload, 
  RefreshCw, 
  ShieldCheck, 
  AlertTriangle,
  Database,
  Globe,
  CheckCircle2,
  Trash2,
  Upload
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn, safeSetItem } from '../../lib/utils';

interface SystemAdminProps {
  onPublish: (data: any) => Promise<void>;
  currentData: any;
}

export const SystemAdmin: React.FC<SystemAdminProps> = ({ onPublish, currentData }) => {
  const isAiStudio = window.location.hostname.includes("run.app") || window.location.hostname.includes("localhost");
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [publishStatus, setPublishStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [deployStatus, setDeployStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [publishError, setPublishError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<string | null>(localStorage.getItem('last_official_publish'));

  const handleSavePreview = async () => {
    setIsPublishing(true);
    setPublishStatus('loading');
    try {
      const response = await fetch('/api/save', { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(currentData)
      });
      if (response.ok) {
        setPublishStatus('success');
        setTimeout(() => setPublishStatus('idle'), 3000);
      } else {
        setPublishStatus('error');
      }
    } catch (error) {
      setPublishStatus('error');
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublish = async () => {
    setIsDeploying(true);
    setDeployStatus('loading');
    try {
      await fetch('/api/save', { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(currentData)
      });
      const response = await fetch('/api/admin/publish', { method: 'POST' });
      if (response.ok) {
        setDeployStatus('success');
        setPublishError(null);
        const now = new Date().toLocaleString('fr-FR');
        safeSetItem('last_official_publish', now);
        setLastSync(now);
        setTimeout(() => setDeployStatus('idle'), 3000);
      } else {
        setDeployStatus('error');
        setPublishError("Une erreur est survenue lors de la publication.");
      }
    } catch (error: any) {
      setDeployStatus('error');
      setPublishError(error.message || "Erreur de connexion au serveur.");
    } finally {
      setIsDeploying(false);
    }
  };

  return (
    <div className="space-y-8 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-zinc-900">Administration</h2>
          <p className="text-zinc-500">Gestion de la publication du site.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          onClick={handleSavePreview}
          disabled={isPublishing || !isAiStudio}
          className={cn(
            "p-6 border border-zinc-100 rounded-3xl transition-all text-left flex items-center gap-4 group",
            !isAiStudio ? "opacity-50 cursor-not-allowed grayscale" : "hover:bg-purple-50"
          )}
        >
          <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-2xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
            {isPublishing ? <RefreshCw className="w-6 h-6 animate-spin" /> : <Database className="w-6 h-6" />}
          </div>
          <div>
            <p className="font-bold text-zinc-900 text-lg">{isPublishing ? "Sauvegarde..." : "Mémoriser Travaux"}</p>
            <p className="text-sm text-zinc-500">{publishStatus === 'success' ? "Enregistré !" : "Enregistre vos modifications en brouillon."}</p>
          </div>
        </button>

        <button
          onClick={handlePublish}
          disabled={isDeploying || !isAiStudio}
          className={cn(
            "p-6 border border-zinc-100 rounded-3xl transition-all text-left flex items-center gap-4 group",
            !isAiStudio ? "opacity-50 cursor-not-allowed grayscale" : "hover:bg-emerald-50"
          )}
        >
          <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
            {isDeploying ? <RefreshCw className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
          </div>
          <div>
            <p className="font-bold text-zinc-900 text-lg">{isDeploying ? "Publication..." : "Publier le Site"}</p>
            <p className="text-sm text-zinc-500">{deployStatus === 'success' ? "Publié avec succès !" : "Met en ligne vos modifications officielles."}</p>
          </div>
        </button>
      </div>
      
      {publishError && (
        <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <p className="text-sm font-medium">{publishError}</p>
        </div>
      )}

      {lastSync && (
        <p className="text-sm text-zinc-400">Dernière publication le : {lastSync}</p>
      )}
    </div>
  );
};
